/* =====================================================================
 * googleOAuthProvider — adapter around the existing B2C Google login
 * helpers.
 *
 * This is the only provider factory backed by real code in Phase 5.
 * The package intentionally does *not* import `api/client.ts`; instead
 * the consuming app passes the concrete `buildLoginUrl`,
 * `exchangeAuthCode`, and (optionally) `exchangeAuthCodeForDesktop`
 * callbacks via `GoogleOAuthProviderConfig`. That keeps
 * `@auw/auth` free of any assumption about how the HTTP layer is
 * organised, which matters because B2B tenants may run the backend on
 * a different origin / version / auth scheme entirely.
 *
 * Behaviour preserved from the legacy code path
 *   - `startLogin` sets `window.location.href` to the URL returned by
 *     the injected builder. Passing a `redirectUri` is optional; when
 *     omitted the backend's configured fallback is used (matches the
 *     old `buildGoogleLoginUrl(undefined)` call).
 *   - `handleCallback` exchanges the code via the web helper by
 *     default; `{ desktop: true }` routes to the desktop helper if
 *     the tenant supplied one.
 * ===================================================================== */

import type {
  AuthProvider,
  AuthSessionResult,
  DesktopAuthSessionResult,
  GoogleOAuthProviderConfig,
  HandleCallbackOptions,
  StartLoginOptions,
} from '../types';

/** Default i18n key used when the tenant doesn't override
 *  `labelKey`. Mirrors the legacy "Continue with Google" button. */
const DEFAULT_LABEL_KEY = 'auth.login.continueWithGoogle';

/** Build a runtime provider from the tenant-supplied config. Wraps
 *  the DI'd functions in `startLogin` / `handleCallback` while
 *  shielding callers from the underlying fetch semantics. */
export function googleOAuthProvider(
  config: GoogleOAuthProviderConfig,
): AuthProvider {
  const labelKey = config.labelKey ?? DEFAULT_LABEL_KEY;

  return {
    id: config.id,
    kind: 'google_oauth',
    labelKey,
    iconSrc: config.iconSrc,

    getLoginUrl(options?: StartLoginOptions) {
      return config.buildLoginUrl(options?.redirectUri);
    },

    startLogin(options?: StartLoginOptions) {
      const url = config.buildLoginUrl(options?.redirectUri);
      if (typeof window !== 'undefined') {
        window.location.href = url;
      }
    },

    async handleCallback(
      options: HandleCallbackOptions,
    ): Promise<AuthSessionResult | DesktopAuthSessionResult> {
      if (options.desktop) {
        if (!config.exchangeAuthCodeForDesktop) {
          throw new Error(
            `[auth] Google provider ${JSON.stringify(
              config.id,
            )} has no desktop exchange handler wired up.`,
          );
        }
        return config.exchangeAuthCodeForDesktop(options.code);
      }
      return config.exchangeAuthCode(options.code);
    },
  };
}
